import { useState } from "react";
import { ArrowUpRight, Check, Loader2, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { formatApiError, publicApi } from "@/lib/api";
import {
  APPLE_SUBSCRIPTIONS_URL,
  GOOGLE_PLAY_SUBSCRIPTIONS_URL,
  MICROSOFT_SUBSCRIPTIONS_URL,
  haloAccount,
} from "@/config/haloAccount";

const deletionEffects = [
  "Revokes Halo SuperVPN access and removes active device associations.",
  "Removes the customer profile fields used to operate the Halo account.",
  "Does not cancel a Microsoft Store, Apple App Store or Google Play subscription automatically.",
  "May retain limited transaction, fraud-prevention and legal records where required.",
];

export default function HaloAccountDeletionRequest() {
  const [email, setEmail] = useState("");
  const [billingSource, setBillingSource] = useState("unknown");
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [requestId, setRequestId] = useState("");
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await publicApi.post("/halo/account-deletion", {
        email,
        billing_source: billingSource,
        privacy_consent: privacyConsent,
        website,
      });
      setRequestId(String(data?.id || ""));
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Delete a Halo Account — LightningFlow"
        description="Start deletion of your Halo SuperVPN account and learn how store subscriptions are handled."
        noIndex
      />
      <section className="border-b border-ink-100 bg-ink-50/50">
        <div className="lf-container py-16 md:py-24">
          <div className="lf-eyebrow">Halo account deletion</div>
          <h1 className="mt-5 max-w-4xl font-display text-5xl font-black leading-tight md:text-6xl">
            Delete Halo SuperVPN access and Halo Account data.
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-ink-600">
            This page lets Halo SuperVPN customers start account deletion without
            reinstalling the app. LightningFlow verifies the request before
            deleting the account so another person cannot delete it.
          </p>
        </div>
      </section>

      <section className="lf-section">
        <div className="lf-container grid gap-10 lg:grid-cols-[1fr_.72fr]">
          <div>
            <h2 className="font-display text-3xl font-black">What deletion does</h2>
            <div className="mt-6 divide-y divide-ink-100 border border-ink-100">
              {deletionEffects.map((effect) => (
                <div key={effect} className="flex gap-4 bg-white p-5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-electric" />
                  <p className="text-sm leading-relaxed text-ink-600">{effect}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 border-l-2 border-amber-500 bg-amber-50 px-5 py-4">
              <h3 className="font-display text-lg font-bold">Cancel billing separately</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-700">
                Deleting a Halo account does not cancel a subscription held by
                Microsoft, Apple or Google. Cancel it with the store that bills
                you to prevent a future renewal.
              </p>
              <div className="mt-4 flex flex-wrap gap-4">
                <a className="lf-link" href={MICROSOFT_SUBSCRIPTIONS_URL} target="_blank" rel="noreferrer">
                  Microsoft subscriptions <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
                <a className="lf-link" href={APPLE_SUBSCRIPTIONS_URL} target="_blank" rel="noreferrer">
                  Apple subscriptions <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
                <a className="lf-link" href={GOOGLE_PLAY_SUBSCRIPTIONS_URL} target="_blank" rel="noreferrer">
                  Google Play subscriptions <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>

          <aside className="h-fit border border-ink-100 bg-white p-7 md:p-8">
            <Trash2 className="h-7 w-7 text-electric" />
            <h2 className="mt-6 font-display text-3xl font-black">Start a request</h2>
            {requestId ? (
              <div className="mt-5 border-l-2 border-electric bg-electric-soft p-5" data-testid="halo-deletion-success">
                <h3 className="font-display text-xl font-bold">Request received</h3>
                <p className="mt-2 text-sm leading-relaxed text-ink-600">
                  We will contact the Halo account email to verify ownership.
                  Reference: <span className="font-mono">{requestId}</span>
                </p>
              </div>
            ) : (
              <form className="mt-5 space-y-5" onSubmit={submit}>
                <label className="block">
                  <span className="lf-label">Halo account email</span>
                  <input
                    className="lf-input"
                    type="email"
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="lf-label">Billing source</span>
                  <select
                    className="lf-input"
                    value={billingSource}
                    onChange={(event) => setBillingSource(event.target.value)}
                  >
                    <option value="unknown">Not sure</option>
                    <option value="microsoft">Microsoft Store</option>
                    <option value="apple">Apple App Store</option>
                    <option value="google_play">Google Play</option>
                    <option value="stripe">LightningFlow / Stripe</option>
                  </select>
                </label>
                <input
                  className="hidden"
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
                <label className="flex gap-3 text-sm leading-relaxed text-ink-600">
                  <input
                    className="mt-1 h-4 w-4 accent-electric"
                    type="checkbox"
                    required
                    checked={privacyConsent}
                    onChange={(event) => setPrivacyConsent(event.target.checked)}
                  />
                  <span>
                    I agree to the <Link className="underline" to="/privacy">privacy handling</Link> needed
                    to verify and complete this request.
                  </span>
                </label>
                {error ? <p className="text-sm text-red-700">{error}</p> : null}
                <button className="lf-btn-primary w-full" type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {submitting ? "Submitting…" : "Request account deletion"}
                </button>
              </form>
            )}
            <p className="mt-4 text-xs leading-relaxed text-ink-500">
              Do not include a password, card number, purchase token or store
              login. LightningFlow will ask only for the minimum information
              needed to verify ownership.
            </p>
            <p className="mt-3 text-xs leading-relaxed text-ink-500">
              If the form is unavailable, email{" "}
              <a className="underline" href={`mailto:${haloAccount.supportEmail}?subject=Halo%20Account%20deletion%20request`}>
                {haloAccount.supportEmail}
              </a>.
            </p>
            <Link className="lf-link mt-6" to="/halo/account">
              Back to Halo account
            </Link>
          </aside>
        </div>
      </section>
    </>
  );
}
